import { EnodeHvacZod } from '@vae/server/src/interfaces/eNode/eNodeHVAC.interface'
import { useContext, useEffect } from 'react'
import { defaultLatitude, defaultLongitude } from '../../../../constants'
import useUser from '../../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../../modules/trpc'

export const useHvacs = () => {
	const utils = trpc.useContext()

	const object = useContext(ObjectContext)

	const { user } = useUser()

	const { data: hvacs } = trpc.eNode.hvac.getAll.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{
			enabled: Boolean(user?.hashedPid) && user?.hvacLinked, // production
			// enabled: Boolean(user?.hashedPid), // development
			staleTime: 1000 * 60 * 5, // 5 minutes
		}
	)

	const { data: hvacSchedules } = trpc.eNode.schedules.getAllSchedules.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{
			enabled: Boolean(user?.hashedPid),
			// staleTime: 1000 * 60 * 2, // 2 minutes
		}
	)

	const { data: locationData } = trpc.eNode.locations.getAllUserLocations.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{ enabled: Boolean(user?.hashedPid) }
	)

	const createSchedule = trpc.eNode.schedules.createSchedule.useMutation({
		onSuccess: () => {
			utils.eNode.schedules.getAllSchedules.invalidate({ hashedPid: user?.hashedPid ?? '' })
		},
	})

	const updateHvacLocation = trpc.eNode.hvac.setLocation.useMutation({})
	const deleteSchedules = trpc.eNode.schedules.deleteSchedule.useMutation({})
	const addLocation = trpc.eNode.locations.createLocation.useMutation()

	const updateLocationIfNeeded = async (hvac: EnodeHvacZod) => {
		if (hvac.locationId) return
		try {
			if (locationData) {
				const homeAddressAttempt = locationData.data.find(
					location => location.name === object.streetAddress
				)

				if (homeAddressAttempt) {
					await updateHvacLocation.mutateAsync({
						hvacId: hvac.id,
						locationId: homeAddressAttempt.id,
					})
					return
				} else {
					const newLocation = await addLocation.mutateAsync({
						hashedPid: user!.hashedPid,
						name: object.streetAddress || 'Koti',
						latitude: defaultLatitude,
						longitude: defaultLongitude,
						timezoneName: 'Europe/Helsinki',
					})
					if (!newLocation?.id) {
						console.error('Error creating location')
						return
					} else {
						updateHvacLocation.mutateAsync({
							hvacId: hvac.id,
							locationId: newLocation?.id,
						})
					}
				}
			}
		} catch (error) {
			console.error('Error updating location for HVAC', error)
		}
	}

	const createUserSchedules = async () => {
		if (!user || !hvacs) return
		const dayRule = {
			targetState: {
				mode: 'HEAT',
				holdType: 'PERMANENT',
				heatSetpoint: 22,
			},
			hourMinute: {
				from: '08:01',
				to: '23:01',
			},
		}
		const nightRule = {
			hourMinute: {
				from: '23:00',
				to: '08:00',
			},

			targetState: {
				mode: 'HEAT',
				holdType: 'PERMANENT',
				heatSetpoint: 20,
			},
		}

		try {
			const createSchedulesForHvac = async (hvacId: string) => {
				console.trace('createSchedulesForHvac', hvacId)
				const existingSchedules = hvacSchedules?.data.filter(
					schedule => schedule.targetId === hvacId
				)

				// Create a schedule with both day and night rules only if there are less than 2 schedules
				const dayAndNightSchedule =
					existingSchedules && existingSchedules.length < 2
						? await createScheduleForHvac(hvacId, [dayRule, nightRule])
						: null

				// Create a schedule with only the day rule only if there are less than 2 schedules
				const onlyDaySchedule =
					existingSchedules && existingSchedules.length < 2
						? await createScheduleForHvac(hvacId, [dayRule])
						: null

				return { dayAndNightSchedule, onlyDaySchedule }
			}

			const allHvacsSchedulesPromises = hvacs.data.map(async hvac =>
				createSchedulesForHvac(hvac.id)
			)
			const allHvacsSchedules = await Promise.all(allHvacsSchedulesPromises)

			utils.eNode.schedules.getAllSchedules.invalidate({ hashedPid: user.hashedPid })

			return allHvacsSchedules
		} catch (error) {
			console.error('Error creating new schedules', error)
		}
	}
	const createScheduleForHvac = async (hvacId: string, rules: any[]) => {
		if (!user) return
		try {
			const scheduleRules = {
				hashedPid: user.hashedPid,
				isEnabled: false,
				targetId: hvacId,
				targetType: 'hvac',
				defaultTargetState: {
					heatSetpoint: 22,
					mode: 'HEAT',
					holdType: 'PERMANENT',
				},
				rules: rules,
			}

			const schedule = await createSchedule.mutateAsync(scheduleRules)

			return schedule
		} catch (error) {
			console.error('Error creating schedule', error)
		}
	}

	const checkSchedules = async (hvac: EnodeHvacZod) => {
		if (!user || !hvacs || !hvacSchedules) return
		try {
			const schedulesForHvac = hvacSchedules.data.filter(schedule => schedule.targetId === hvac.id)

			const sortedSchedules = schedulesForHvac.sort((a, b) => a.id.localeCompare(b.id))

			const latestSchedules = sortedSchedules.slice(-2)

			const schedulesToDelete = schedulesForHvac.filter(
				schedule => !latestSchedules.some(latest => latest.id === schedule.id)
			)

			if (schedulesToDelete.length > 0) {
				await Promise.all(
					schedulesToDelete.map(schedule =>
						deleteSchedules.mutateAsync({ hashedPid: user.hashedPid, scheduleId: schedule.id })
					)
				)
			}
			if (latestSchedules.length < 2) {
				await createUserSchedules()
			}
		} catch (error) {
			console.error('Error checking or removing schedules', error)
		}
	}

	useEffect(() => {
		if (!object.streetAddress || !hvacs) return

		const fetchData = async () => {
			try {
				await Promise.all(hvacs.data.map(hvac => updateLocationIfNeeded(hvac)))

				// Check schedules for all hvacs
				await Promise.all(hvacs.data.map(hvac => checkSchedules(hvac)))
			} catch (error) {
				console.error('Error fetching data:', error)
			}
		}

		fetchData()
	}, [hvacs?.data])

	return {
		hvacs,
		hvacSchedules,
	}
}
